import React from 'react';
import { cn } from '../../utils/cn';
import { useFieldControl } from './Field';

// Colour and state classes for every text-like control, kept in one place.
const TONES = {
    base: 'bg-surface text-fg placeholder:text-fg-subtle',
    border: 'border-line',
    focus: 'outline-none focus-visible:ring-2 focus-visible:ring-focus',
    focusBorder: 'focus-visible:border-primary-500',
    invalid: 'border-danger-line focus-visible:ring-danger-line',
    disabled: 'disabled:bg-surface-muted disabled:text-fg-muted',
};

// compact: toolbar/account-page look (h-9). form: modal and settings forms. number: settings rows.
const SIZES = {
    compact: `flex h-9 w-full rounded-control px-3 py-1 shadow-sm transition-colors ${TONES.focusBorder} disabled:pointer-events-none disabled:opacity-50`,
    form: `w-full rounded-control px-3 py-2 transition-shadow ${TONES.disabled}`,
    number: `w-28 rounded-control px-2.5 py-1.5 text-right tabular-nums ${TONES.disabled}`,
};

/**
 * Shared class list for text-like controls. Width lives in the size, so callers needing a
 * narrower control size its container rather than passing a competing width class.
 */
const controlClass = ({ size = 'form', invalid, className }) =>
    cn(
        'border text-sm disabled:cursor-not-allowed',
        TONES.base,
        TONES.focus,
        SIZES[size],
        invalid ? TONES.invalid : TONES.border,
        className
    );

/** Compact input (h-9). Kept for the account pages and the new-user form. */
export function Input({ className, type, invalid, ...props }) {
    const { invalid: isInvalid, ...a11y } = useFieldControl({
        id: props.id,
        invalid,
        'aria-describedby': props['aria-describedby'],
    });
    return (
        <input
            type={type}
            {...props}
            {...a11y}
            className={controlClass({ size: 'compact', invalid: isInvalid, className })}
        />
    );
}

/**
 * Standard form text input. Inside a <Field> it is labelled and described automatically.
 * @param {boolean} [invalid] error styling + aria-invalid (implied by Field `error`)
 */
export function TextInput({ className, type = 'text', invalid, ...props }) {
    const { invalid: isInvalid, ...a11y } = useFieldControl({
        id: props.id,
        invalid,
        'aria-describedby': props['aria-describedby'],
    });
    return <input type={type} {...props} {...a11y} className={controlClass({ invalid: isInvalid, className })} />;
}

/** Multi-line text input with the form look. */
export function Textarea({ className, invalid, ...props }) {
    const { invalid: isInvalid, ...a11y } = useFieldControl({
        id: props.id,
        invalid,
        'aria-describedby': props['aria-describedby'],
    });
    return <textarea {...props} {...a11y} className={controlClass({ invalid: isInvalid, className })} />;
}

/**
 * Native select with the form look.
 * @param {Array<{value: string, label: string}>} [options] rendered after any children
 * @param {string} [placeholder] first, empty option
 */
// Native arrow hugs the right edge. appearance-none plus this chevron (neutral grey, readable on
// light and dark surfaces) sits 12px in from the border, with padding so text never runs under it.
const SELECT_CHEVRON = `url("data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="#64748b" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m4 6 4 4 4-4"/></svg>'
)}")`;

export function Select({ className, invalid, options, placeholder, children, style, ...props }) {
    const { invalid: isInvalid, ...a11y } = useFieldControl({
        id: props.id,
        invalid,
        'aria-describedby': props['aria-describedby'],
    });
    return (
        <select
            {...props}
            {...a11y}
            style={{
                backgroundImage: SELECT_CHEVRON,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                ...style,
            }}
            className={controlClass({ invalid: isInvalid, className: cn('appearance-none pr-9 forced-colors:appearance-auto forced-colors:!bg-none', className) })}
        >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {children}
            {options?.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    );
}

/**
 * Compact right-aligned number input for settings rows. `onChange` receives the raw string
 * value ('' when cleared), matching how app_config numbers are stored in form state.
 */
export function NumberInput({ value, onChange, className, min = '0', invalid, ...props }) {
    const { invalid: isInvalid, ...a11y } = useFieldControl({
        id: props.id,
        invalid,
        'aria-describedby': props['aria-describedby'],
    });
    return (
        <input
            type="number"
            min={min}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            {...props}
            {...a11y}
            className={controlClass({ size: 'number', invalid: isInvalid, className })}
        />
    );
}
